export type EnergyStudyIdempotencyStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type PendingValue = { idempotencyKey: string; fingerprint: string };

function canonicalJson(value: any): string {
  if (value === undefined) return "null";
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.keys(value).filter(key => value[key] !== undefined).sort().map(key => `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(",")}}`;
  return JSON.stringify(value);
}

export async function energyStudyPayloadFingerprint(value: unknown): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalJson(value)));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
}

export async function getOrCreateEnergyStudyIdempotencyKey(requestId: number, payload: unknown, storage: EnergyStudyIdempotencyStorage, uuid = () => globalThis.crypto.randomUUID()): Promise<string> {
  const storageKey = `ods:ev-energy-study:pending:${requestId}`;
  const payloadFingerprint = await energyStudyPayloadFingerprint(payload);
  try {
    const saved = storage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<PendingValue>;
      if (parsed.fingerprint === payloadFingerprint && typeof parsed.idempotencyKey === "string") return parsed.idempotencyKey;
    }
  } catch { /* storage corruption/unavailability cannot weaken server-side binding */ }
  const idempotencyKey = uuid();
  try { storage.setItem(storageKey, JSON.stringify({ idempotencyKey, fingerprint: payloadFingerprint } satisfies PendingValue)); } catch { /* in-memory caller can still retry */ }
  return idempotencyKey;
}

export function clearEnergyStudyIdempotencyKey(requestId: number, storage: EnergyStudyIdempotencyStorage): void {
  try { storage.removeItem(`ods:ev-energy-study:pending:${requestId}`); } catch { /* best effort after server returned the committed result */ }
}
